import React from "react";
import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Menu,
  ChevronDown,
  User,
  LogOut,
  HelpCircle,
  LifeBuoy,
  Shield,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useAuth } from "react-oidc-context";
import { usePageIcon, usePageTitle } from "../contexts/page-title-context";
import { usePermissions } from "../contexts/permissions-context";
import { ImportProgressWidget } from "./import-progress-widget";
import { MuiSidebar } from "./layout/mui-sidebar";
import { MobileSidebar } from "./layout/mobile-sidebar";
import { ThemeToggleMenu } from "./shared/theme-toggle-menu";
import { JobsStatusIcon } from "./jobs/JobsStatusIcon";
// import { OnboardingOverlay } from "@/components/onboarding/onboarding-overlay" // Desactivado
export default function ClientLayout({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [isPinned, setIsPinned] = useState(() => {
    // Recuperar estado desde localStorage
    const saved = localStorage.getItem("nemesis-sidebar-pinned");
    return saved === "true";
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { title } = usePageTitle();
  const { icon } = usePageIcon();
  const { userRole, isSuperadmin } = usePermissions();
  const profile: any = auth?.user?.profile || null;

  const ROLE_LABELS: Record<string, string> = {
    SUPERADMIN: "Superadmin",
    ADMINISTRADOR: "Administrador",
    EDITOR: "Editor",
    USUARIO: "Usuario",
  };

  const currentUser = profile
    ? {
        email: profile.email ?? "",
        username:
          profile.preferred_username ??
          profile.name ??
          (profile.email ? String(profile.email).split("@")[0] : "Usuario"),
        first_name: profile.given_name ?? null,
        last_name: profile.family_name ?? null,
        avatar_url: profile.picture ?? null,
        roleLabel: "Usuario",
      }
    : null;
  const handleLogout = () => {
    // Usar el método de signout de OIDC que redirige al proveedor de identidad
    auth?.signoutRedirect();
  };
  // Persistir estado del pin en localStorage
  useEffect(() => {
    localStorage.setItem("nemesis-sidebar-pinned", isPinned.toString());
  }, [isPinned]);
  const handleTogglePin = () => {
    setIsPinned(!isPinned);
  };
  // Determinar el ancho del sidebar
  const sidebarWidth = isPinned ? 280 : 60;
  // El sidebar ahora se controla solo por hover, no necesita eventos
  // Onboarding desactivado
  // useEffect(() => {
  //   if (!isClient) return
  //
  //   const hasCompletedOnboarding = localStorage.getItem("nemesis-onboarding-completed")
  //   if (!hasCompletedOnboarding) {
  //     setTimeout(() => setShowOnboarding(true), 500)
  //   }
  // }, [isClient])
  // const handleOnboardingComplete = () => {
  //   setShowOnboarding(false)
  //   if (isClient) {
  //     localStorage.setItem("nemesis-onboarding-completed", "true")
  //   }
  // }
  return (
    <div
      className="relative min-h-screen w-full"
      suppressHydrationWarning={true}
    >
      {/* Sidebar MUI fijo - se controla solo con el botón toggle */}
      <div
        className="fixed left-0 top-0 z-50 hidden h-screen md:block"
        style={{ width: sidebarWidth }}
      >
        <MuiSidebar
          isPinned={isPinned}
          onTogglePin={handleTogglePin}
        />
      </div>
      {/* Header fijo - ajusta su padding según el estado del sidebar */}
      <header 
        className="fixed top-0 z-40 flex w-full h-14 items-center border-b bg-background transition-all duration-300"
        style={{ 
          paddingLeft: window.innerWidth >= 768 ? `${sidebarWidth}px` : '0px'
        }}
      >
        <div className="flex flex-1 items-center gap-4 px-4 lg:px-6">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="shrink-0 md:hidden bg-transparent"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex flex-col p-0 w-[280px]">
              <MobileSidebar onClose={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex-1 flex items-center gap-2">
            {icon &&
              React.createElement(icon, {
                className: "h-5 w-5 text-foreground",
                "aria-hidden": "true",
              })}
            <h1 className="text-lg font-semibold">{title}</h1>
          </div>
          <JobsStatusIcon />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-3 px-3 py-2 h-auto hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-foreground"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={currentUser?.avatar_url || undefined}
                      alt={currentUser?.username}
                    />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {(
                        currentUser?.first_name?.[0] ||
                        currentUser?.username?.[0] ||
                        "U"
                      ).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium">
                      {currentUser?.first_name
                        ? `${currentUser.first_name} ${
                            currentUser.last_name || ""
                          }`
                        : currentUser?.username || "Usuario"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentUser?.roleLabel || "Invitado"}
                    </p>
                  </div>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link
                  to="/perfil"
                  className="cursor-pointer flex w-full items-center"
                >
                  <User className="mr-2 h-4 w-4" />
                  Perfil
                </Link>
              </DropdownMenuItem>
              {isSuperadmin && (
                <DropdownMenuItem asChild>
                  <Link
                    to="/mis-permisos"
                    className="cursor-pointer flex w-full items-center"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    Mis Permisos
                  </Link>
                </DropdownMenuItem>
              )}
              {/* <ThemeToggleMenu /> */}
              <DropdownMenuSeparator />
              {/* <DropdownMenuItem asChild>
                <Link
                  to="/ayuda"
                  className="cursor-pointer flex w-full items-center"
                >
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Centro de Ayuda
                </Link>
              </DropdownMenuItem> */}
              {/* <DropdownMenuItem asChild>
                <Link
                  to="/ayuda/soporte"
                  className="cursor-pointer flex w-full items-center"
                >
                  <LifeBuoy className="mr-2 h-4 w-4" />
                  Soporte Técnico
                </Link>
              </DropdownMenuItem> */}
              {/* <DropdownMenuItem disabled className="opacity-50 cursor-not-allowed">
                <Activity className="mr-2 h-4 w-4" />
                Ver Tour de la App
              </DropdownMenuItem> */}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      {/* Contenido principal con scroll independiente */}
      <main 
        className="fixed top-14 h-[calc(100vh-56px)] overflow-y-auto bg-gradient-to-br from-slate-100 via-blue-50 to-slate-200 transition-all duration-300"
        style={{
          left: window.innerWidth >= 768 ? `${sidebarWidth}px` : '0px',
          width: window.innerWidth >= 768 ? `calc(100vw - ${sidebarWidth}px)` : '100vw'
        }}
      >
        <div className="p-2 md:p-8 h-full w-full max-w-none">{children}</div>
      </main>
      {/* Onboarding desactivado */}
      {/* <OnboardingOverlay isActive={showOnboarding} onComplete={handleOnboardingComplete} /> */}
      <ImportProgressWidget />
    </div>
  );
}
